import { useEffect, useRef, useState } from 'react';
import { api } from './api';
import { formatMoney, netPnl } from './pnl';
import { PRESETS_CHECKED, firmsForPlatform } from './balance';
import { FirmSearch, PlanPicker } from './FirmPicker';
import { currentLocale, translateNow } from './langState';
import { useI18n } from './i18n';
import FeeRuleForm, { FeeRuleSummary } from './FeeRules';
import { ArchiveAccountDialog, asksHowItEnded } from './AccountEnded';

export const PLATFORMS = ['TopstepX', 'Tradovate', 'NinjaTrader', 'Rithmic', 'Tradesea', 'Other'];
export const ACCOUNT_TYPES = ['Evaluation', 'Funded', 'Live', 'Personal'];

// Shows only the last 4 characters of an account number: ••1613
export function maskNumber(number) {
  const s = String(number || '');
  return s.length > 4 ? `••${s.slice(-4)}` : s;
}

export function accountLabel(account) {
  return account ? account.name : translateNow('Unknown account');
}

// Default name for an account first seen in an import, e.g. "Tradovate 1613".
export function suggestedName(number, platform) {
  const tail = String(number).slice(-4);
  return platform && platform !== 'Other' ? `${platform} ${tail}` : `${translateNow('Account')} ${tail}`;
}

export function AccountSwitcher({ accounts, selected, onSelect, onManage, totalTrades }) {
  const { t } = useI18n();
  const active = accounts.filter((a) => !a.archived);
  return (
    <div className="account-switcher" role="tablist" aria-label={t('Accounts')}>
      <button
        type="button"
        role="tab"
        aria-selected={selected === 'all'}
        className={`account-pill${selected === 'all' ? ' active' : ''}`}
        onClick={() => onSelect('all')}
      >
        {t('All accounts')} <span className="account-count">{totalTrades}</span>
      </button>
      {active.map((a) => (
        <button
          key={a._id}
          type="button"
          role="tab"
          aria-selected={selected === a._id}
          className={`account-pill${selected === a._id ? ' active' : ''}`}
          onClick={() => onSelect(a._id)}
          title={a.externalId ? `${t('Account')} ${maskNumber(a.externalId)}` : undefined}
        >
          {a.name} <span className="account-count">{a.tradeCount}</span>
        </button>
      ))}
      <button type="button" className="link-button account-manage" onClick={onManage}>
        {t('Manage accounts')}
      </button>
    </div>
  );
}

const RULE_FIELDS = [
  'size', 'profitTarget', 'maxLossLimit', 'dailyLossLimit', 'consistencyLimit', 'payoutWinningDays', 'payoutMinDayProfit',
  'trailLockOffset',
];

function formFromAccount(a) {
  const f = {
    name: a.name || '',
    externalId: a.externalId || '',
    platform: a.platform || 'Other',
    type: a.type || '',
    drawdownType: a.drawdownType || 'trailing_eod',
    consistencyBase: a.consistencyBase || 'total',
  };
  for (const key of RULE_FIELDS) f[key] = a[key] ? String(a[key]) : '';
  return f;
}

function bodyFromForm(f) {
  const body = {
    name: f.name.trim(),
    externalId: f.externalId.trim(),
    platform: f.platform,
    type: f.type || null,
    drawdownType: f.maxLossLimit ? f.drawdownType : null,
    consistencyBase: f.consistencyLimit ? f.consistencyBase : null,
  };
  for (const key of RULE_FIELDS) body[key] = f[key] === '' ? null : Number(f[key]);
  // Personal accounts only track their balance.
  if (f.type === 'Personal') {
    for (const key of RULE_FIELDS) if (key !== 'size') body[key] = null;
    body.drawdownType = null;
    body.consistencyBase = null;
  }
  return body;
}

// The editable fields shared by "add account" and each existing account.
function AccountFields({ form, onChange }) {
  const { t, tr } = useI18n();
  function set(e) {
    onChange({ ...form, [e.target.name]: e.target.value });
  }
  function applyPreset(preset) {
    if (!preset) return;
    const str = (v) => (v ? String(v) : '');
    onChange({
      ...form,
      // Only Topstep presets imply a platform; other firms run on several.
      ...(preset.platform ? { platform: preset.platform } : {}),
      type: preset.type,
      size: str(preset.size),
      profitTarget: str(preset.profitTarget),
      maxLossLimit: str(preset.maxLossLimit),
      drawdownType: preset.drawdownType,
      trailLockOffset: str(preset.trailLockOffset),
      dailyLossLimit: str(preset.dailyLossLimit),
      consistencyLimit: str(preset.consistencyLimit),
      consistencyBase: preset.consistencyBase || 'total',
      payoutWinningDays: str(preset.payoutWinningDays),
      payoutMinDayProfit: str(preset.payoutMinDayProfit),
    });
    setPresetUsed(preset.label);
    setPresetId(preset.id);
  }
  const [presetUsed, setPresetUsed] = useState('');
  const [presetId, setPresetId] = useState('');
  const [presetFirm, setPresetFirm] = useState('');
  const isPersonal = form.type === 'Personal';

  return (
    <>
      <div className="account-fields">
        <label>
          {t('Name')}
          <input name="name" value={form.name} onChange={set} maxLength={60} placeholder={t('e.g. Topstep 50K #2')} />
        </label>
        <label>
          {t('Account number')}
          <input name="externalId" value={form.externalId} onChange={set} maxLength={80} placeholder={t('From your CSV')} />
        </label>
        <label>
          {t('Platform')}
          <select name="platform" value={form.platform} onChange={set}>
            {PLATFORMS.map((p) => <option key={p} value={p}>{t(p)}</option>)}
          </select>
        </label>
        <label>
          {t('Type')}
          <select name="type" value={form.type} onChange={set}>
            <option value="">{t('Not set')}</option>
            {ACCOUNT_TYPES.map((x) => <option key={x} value={x}>{t(x)}</option>)}
          </select>
        </label>
      </div>
      <div className="account-fields">
        <label>
          {t('Starting balance ($)')}
          <input name="size" type="number" min="0" step="any" value={form.size} onChange={set} placeholder={t('e.g. 500 or 50000')} />
        </label>
        {!isPersonal && (
          <div className="preset-pick">
            <span className="preset-pick-label">{t('Load a firm’s rules')}</span>
            <FirmSearch id="preset-firm" value={presetFirm} allowNone={false} firms={firmsForPlatform(form.platform)} onChange={(f) => { setPresetFirm(f); setPresetId(''); }} />
            {presetFirm && <PlanPicker key={presetFirm} firm={presetFirm} presetId={presetId} onPick={applyPreset} />}
          </div>
        )}
      </div>
      {!isPersonal && (
        <p className={`preset-note${presetUsed ? ' applied' : ''}`}>
          {presetUsed ? <>{tr('<0>{name}</0> filled in.', [(x) => <strong>{x}</strong>], { name: presetUsed })} </> : null}
          {t("Presets use each firm's published rules as of {date}. Firms change their rules often, so check the numbers against your firm's dashboard and edit anything that differs.", { date: t(PRESETS_CHECKED) })}
        </p>
      )}
      {!isPersonal && (
        <div className="account-fields">
          <label>
            {t('Profit target ($)')}
            <input name="profitTarget" type="number" min="0" step="any" value={form.profitTarget} onChange={set} placeholder={t('e.g. 3000')} />
          </label>
          <label>
            {t('Max loss limit ($)')}
            <input name="maxLossLimit" type="number" min="0" step="any" value={form.maxLossLimit} onChange={set} placeholder={t('e.g. 2000')} />
          </label>
          <label>
            {t('Max loss type')}
            <select name="drawdownType" value={form.drawdownType} onChange={set} disabled={!form.maxLossLimit}>
              <option value="trailing_eod">{t('Trailing, EOD')}</option>
              <option value="trailing_intraday">{t('Trailing, intraday')}</option>
              <option value="static">{t('Static')}</option>
            </select>
          </label>
          <label>
            {t('Daily loss limit ($)')}
            <input name="dailyLossLimit" type="number" min="0" step="any" value={form.dailyLossLimit} onChange={set} placeholder={t('Optional')} />
          </label>
        </div>
      )}
      {!isPersonal && (
        <div className="account-fields">
          <label>
            {t('Trail stops at start + $')}
            <input name="trailLockOffset" type="number" min="0" step="any" value={form.trailLockOffset} onChange={set}
              placeholder="0" disabled={!form.maxLossLimit || form.drawdownType === 'static'} />
          </label>
          <label>
            {t('Consistency limit (%)')}
            <input name="consistencyLimit" type="number" min="1" max="100" step="any" value={form.consistencyLimit} onChange={set} placeholder={t('e.g. 55')} />
          </label>
          <label>
            {t('Consistency based on')}
            <select name="consistencyBase" value={form.consistencyBase} onChange={set} disabled={!form.consistencyLimit}>
              <option value="total">{t('Total profit')}</option>
              <option value="target">{t('Profit target')}</option>
            </select>
          </label>
          <label>
            {t('Payout: winning days')}
            <input name="payoutWinningDays" type="number" min="1" step="1" value={form.payoutWinningDays} onChange={set} placeholder={t('e.g. 5')} />
          </label>
          <label>
            {t('Payout: min day profit ($)')}
            <input name="payoutMinDayProfit" type="number" min="0" step="any" value={form.payoutMinDayProfit} onChange={set} placeholder={t('e.g. 150')} />
          </label>
        </div>
      )}
    </>
  );
}

// A permanent action the trader must confirm by typing a phrase, so it can't
// happen with a stray click.
function DangerConfirm({ title, phrase, confirmLabel, busy, onConfirm, onCancel, children }) {
  const { t, tr } = useI18n();
  const [typed, setTyped] = useState('');
  const matches = typed.trim().toLowerCase() === phrase.trim().toLowerCase();
  return (
    <div className="danger-confirm" role="group" aria-label={title}>
      <strong className="danger-confirm-title">{title}</strong>
      <p>{children}</p>
      <label>
        <span>{tr('Type <0>{phrase}</0> to confirm', [(x) => <strong>{x}</strong>], { phrase })}</span>
        <input value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" spellCheck={false}
          autoFocus onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }} />
      </label>
      <div className="account-row-actions">
        <button type="button" className="danger small" onClick={onConfirm} disabled={!matches || busy}>
          {busy ? t('Deleting…') : confirmLabel}
        </button>
        <button type="button" className="link-button" onClick={onCancel} disabled={busy}>{t('Cancel')}</button>
      </div>
    </div>
  );
}

// "1 trade" / "5 trades" (and "all 5 trades" / "the 1 trade") in the current language.
const tradesText = (n) => translateNow(n === 1 ? '{n} trade' : '{n} trades').replace('{n}', n);
const allTradesText = (n) => translateNow(n === 1 ? 'the 1 trade' : 'all {n} trades').replace('{n}', n);
const attemptsText = (n) => translateNow(n === 1 ? '{n} past attempt' : '{n} past attempts').replace('{n}', n);

const OUTCOMES = [
  { id: 'failed', label: 'Failed', hint: 'Hit a loss limit or broke a rule' },
  { id: 'passed', label: 'Passed', hint: 'Reached the target or got funded' },
  { id: 'restarted', label: 'Just restarting', hint: 'Reset for another reason' },
];
const OUTCOME_LABEL = Object.fromEntries(OUTCOMES.map((o) => [o.id, o.label]));

function shortDate(d) {
  return d ? new Date(d).toLocaleDateString(currentLocale(), { month: 'short', day: 'numeric', year: 'numeric' }) : '';
}

// Saves the current trades as a past attempt and restarts the account with the same rules.
function NewAttemptPanel({ account, busy, onStart, onCancel }) {
  const { t, tr } = useI18n();
  const [outcome, setOutcome] = useState('');
  const [note, setNote] = useState('');
  const number = account.attempt || 1;
  return (
    <div className="attempt-panel" role="group" aria-label={t('Start a new attempt')}>
      <strong className="attempt-panel-title">{t('How did attempt {n} end?', { n: number })}</strong>
      <div className="impact-buttons attempt-outcomes">
        {OUTCOMES.map((o) => (
          <button key={o.id} type="button" className={`impact-button outcome-${o.id}${outcome === o.id ? ' selected' : ''}`}
            onClick={() => setOutcome(o.id)} title={t(o.hint)}>
            {t(o.label)}
          </button>
        ))}
      </div>
      <label>
        <span>{t('What happened? (optional)')}</span>
        <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={500}
          placeholder={t('e.g. Revenge traded after two losses and hit the daily limit')} />
      </label>
      <p>
        {tr('Your {trades} move into <0>Attempt {n}</0>, and {name} starts again at its starting balance with the same rules and account number. Nothing is deleted: past attempts still count in All accounts and Insights.',
          [(x) => <strong>{x}</strong>], { trades: tradesText(account.tradeCount), n: number, name: account.name })}
      </p>
      <div className="account-row-actions">
        <button type="button" className="small" onClick={() => onStart(outcome, note)} disabled={!outcome || busy}>
          {busy ? t('Saving…') : t('Start attempt {n}', { n: number + 1 })}
        </button>
        <button type="button" className="link-button" onClick={onCancel} disabled={busy}>{t('Cancel')}</button>
      </div>
    </div>
  );
}

// The account's earlier attempts, newest first, with how each one went.
function PastAttempts({ attempts, trades, onDeleted, onSessionExpired }) {
  const { t } = useI18n();
  const [confirmId, setConfirmId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  if (!attempts.length) return null;

  async function remove(id) {
    setBusy(true);
    setError('');
    try {
      await api(`/accounts/${id}`, { method: 'DELETE' });
      setConfirmId(null);
      onDeleted(id);
    } catch (err) {
      if (err.status === 401) return onSessionExpired();
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="past-attempts">
      <span className="modal-section-label">{t('Past attempts')}</span>
      <ul>
        {attempts.map((a) => {
          const list = trades.filter((x) => x.accountId === a._id);
          const net = list.reduce((sum, x) => sum + netPnl(x), 0);
          return (
            <li key={a._id}>
              <div className="past-attempt-row">
                <strong>{t('Attempt {n}', { n: a.attemptNumber })}</strong>
                <span className={`outcome-badge outcome-${a.outcome}`}>{t(OUTCOME_LABEL[a.outcome] || 'Ended')}</span>
                <span className="past-attempt-dates">{shortDate(a.attemptStartedAt)} – {shortDate(a.attemptEndedAt)}</span>
                <span className="past-attempt-count">{tradesText(list.length)}</span>
                <span className={`past-attempt-net ${net >= 0 ? 'gain' : 'loss'}`}>{formatMoney(net)}</span>
                <button type="button" className="link-button danger-link" onClick={() => setConfirmId(a._id)} disabled={busy}>{t('Delete')}</button>
              </div>
              {a.attemptNote && <p className="past-attempt-note">{a.attemptNote}</p>}
              {confirmId === a._id && (
                <DangerConfirm title={t('Delete attempt {n}?', { n: a.attemptNumber })} phrase={t('attempt {n}', { n: a.attemptNumber })} busy={busy}
                  confirmLabel={t('Delete attempt and {trades}', { trades: tradesText(list.length) })} onConfirm={() => remove(a._id)}
                  onCancel={() => setConfirmId(null)}>
                  {t("This permanently deletes this past attempt and its {trades}, so they no longer count in All accounts or Insights. This can't be undone.", { trades: tradesText(list.length) })}
                </DangerConfirm>
              )}
            </li>
          );
        })}
      </ul>
      {error && <p className="error">{error}</p>}
    </div>
  );
}

function linkMessage(saved) {
  const parts = [];
  if (saved.mergedAccount) parts.push(translateNow('Accounts merged.'));
  if (saved.movedTrades) {
    parts.push(translateNow('Linked {trades} with this account number.').replace('{trades}', tradesText(saved.movedTrades)));
  }
  return parts.join(' ');
}

function AccountRow({ account, pastAttempts, trades, onSaved, onMerged, onDeleted, onTradesCleared, onAttemptStarted, onFeesSaved, onSessionExpired }) {
  const { t, tr } = useI18n();
  const [editingFees, setEditingFees] = useState(false);
  const [ending, setEnding] = useState(false);
  const [form, setForm] = useState(() => formFromAccount(account));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [conflict, setConflict] = useState(null);
  const [confirm, setConfirm] = useState(null); // 'clear' | 'delete' | 'attempt'
  const dirty = JSON.stringify(form) !== JSON.stringify(formFromAccount(account));

  async function save(extra = {}) {
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const saved = await api(`/accounts/${account._id}`, { method: 'PATCH', body: { ...bodyFromForm(form), ...extra } });
      setConflict(null);
      setForm(formFromAccount(saved));
      setNotice(linkMessage(saved));
      if (saved.mergedAccount || saved.movedTrades) onMerged(saved);
      else onSaved(saved);
    } catch (err) {
      if (err.status === 401) return onSessionExpired();
      if (err.status === 409 && err.data && err.data.conflict) setConflict(err.data.conflict);
      else setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function clearTrades() {
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const { deletedTrades } = await api(`/accounts/${account._id}/trades`, { method: 'DELETE' });
      setConfirm(null);
      setNotice(t('Deleted {trades}. {name} is ready for a fresh start.', { trades: tradesText(deletedTrades), name: account.name }));
      onTradesCleared(account._id);
    } catch (err) {
      if (err.status === 401) return onSessionExpired();
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function startAttempt(outcome, note) {
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const { account: updated } = await api(`/accounts/${account._id}/new-attempt`, { method: 'POST', body: { outcome, note } });
      setConfirm(null);
      setNotice(t('Attempt {done} saved. {name} is on attempt {n}, starting fresh. Good luck!', { done: updated.attempt - 1, name: account.name, n: updated.attempt }));
      onAttemptStarted();
    } catch (err) {
      if (err.status === 401) return onSessionExpired();
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    setSaving(true);
    setError('');
    try {
      await api(`/accounts/${account._id}`, { method: 'DELETE' });
      onDeleted(account._id);
    } catch (err) {
      if (err.status === 401) return onSessionExpired();
      setError(err.message);
      setSaving(false);
    }
  }

  return (
    <li className={`account-row${account.archived ? ' archived' : ''}`}>
      <div className="account-row-head">
        <strong>{account.name}</strong>
        {account.externalId && <span className="account-number">{maskNumber(account.externalId)}</span>}
        <span className="account-trades">{tradesText(account.tradeCount)}</span>
        {(account.attempt > 1 || pastAttempts.length > 0) && <span className="inbox-tag attempt-tag">{t('Attempt {n}', { n: account.attempt || 1 })}</span>}
        {account.archived && <span className="inbox-tag">{t('Archived')}</span>}
      </div>
      <AccountFields form={form} onChange={setForm} />
      <div className="account-fees">
        <span className="modal-section-label">{t('Commissions & fees')}</span>
        {editingFees ? (
          <FeeRuleForm account={account} trades={trades.filter((x) => String(x.accountId) === String(account._id))}
            onSaved={(result) => { setEditingFees(false); onFeesSaved(result); }} onCancel={() => setEditingFees(false)} onSessionExpired={onSessionExpired} />
        ) : (
          <p className="hint">
            {account.feeRule ? <FeeRuleSummary rule={account.feeRule} /> : t('None saved. Trades keep the fees from your file.')}{' '}
            <button type="button" className="link-button" onClick={() => setEditingFees(true)}>{account.feeRule ? t('Edit') : t('Set fees')}</button>
          </p>
        )}
      </div>
      {conflict && (
        <div className="merge-confirm">
          <p>
            {tr('Account number {number} already belongs to <0>{other}</0>{count}. Merge it into <1>{name}</1>? Its trades move here and "{other}" is removed.',
              [(x) => <strong>{x}</strong>, (x) => <strong>{x}</strong>],
              { number: form.externalId, other: conflict.name, count: conflict.tradeCount ? ` (${tradesText(conflict.tradeCount)})` : '', name: account.name })}
          </p>
          <div className="account-row-actions">
            <button type="button" className="small" onClick={() => save({ merge: true })} disabled={saving}>{t('Merge accounts')}</button>
            <button type="button" className="link-button" onClick={() => setConflict(null)}>{t('Cancel')}</button>
          </div>
        </div>
      )}
      {error && <p className="error">{error}</p>}
      {notice && <p className="success">{notice}</p>}
      <div className="account-row-actions">
        <button type="button" className="small" onClick={() => save()} disabled={!dirty || saving || !form.name.trim()}>
          {t('Save')}
        </button>
        <button type="button" className="secondary small" onClick={() => (asksHowItEnded(account) ? setEnding(true) : save({ archived: !account.archived }))} disabled={saving}>
          {account.archived ? t('Unarchive') : t('Archive')}
        </button>
        <button type="button" className="secondary small" onClick={() => setConfirm('attempt')}
          disabled={saving || !account.tradeCount} title={account.tradeCount ? t('Save these trades as a past attempt and start fresh') : t('No trades in this account')}>
          {t('Start new attempt')}
        </button>
        <button type="button" className="secondary small" onClick={() => setConfirm('clear')}
          disabled={saving || !account.tradeCount} title={account.tradeCount ? undefined : t('No trades in this account')}>
          {t('Clear trades')}
        </button>
        <div className="modal-delete">
          <button type="button" className="link-button danger-link" onClick={() => setConfirm('delete')} disabled={saving}>
            {t('Delete account')}
          </button>
        </div>
      </div>
      {confirm === 'attempt' && (
        <NewAttemptPanel account={account} busy={saving} onStart={startAttempt} onCancel={() => setConfirm(null)} />
      )}
      {confirm === 'clear' && (
        <DangerConfirm title={t('Clear all trades in {name}?', { name: account.name })} phrase={account.name} busy={saving}
          confirmLabel={t('Delete {trades}', { trades: tradesText(account.tradeCount) })} onConfirm={clearTrades} onCancel={() => setConfirm(null)}>
          {t("This permanently deletes {trades} in this account, so its balance and rules start over. The account and its settings stay, and so do your journal notes, routine and goals. This can't be undone.", { trades: allTradesText(account.tradeCount) })}
        </DangerConfirm>
      )}
      {confirm === 'delete' && (
        <DangerConfirm title={t('Delete {name}?', { name: account.name })} phrase={account.name} busy={saving}
          confirmLabel={t('Delete account')} onConfirm={remove} onCancel={() => setConfirm(null)}>
          {t('This permanently deletes the account')}
          {account.tradeCount ? ` ${t('and its {trades}', { trades: tradesText(account.tradeCount) })}` : ''}
          {pastAttempts.length ? `, ${t('plus {attempts} and their trades', { attempts: attemptsText(pastAttempts.length) })}` : ''}.{' '}
          {t("This can't be undone.")}
          {!account.archived && (
            <span className="keep-instead">
              <strong>{t('Keep it for your journey?')}</strong>
              <span>{t('Archive hides the account but keeps its trades, what it cost and how it ended in My Trading Journey. Delete erases all of that for good.')}</span>
              <button type="button" className="small" disabled={saving} onClick={() => { setConfirm(null); if (asksHowItEnded(account)) setEnding(true); else save({ archived: true }); }}>{t('Archive instead')}</button>
            </span>
          )}
        </DangerConfirm>
      )}
      <PastAttempts attempts={pastAttempts} trades={trades} onDeleted={onDeleted} onSessionExpired={onSessionExpired} />
      {ending && (
        <ArchiveAccountDialog account={account} trades={trades.filter((x) => String(x.accountId) === String(account._id))} onClose={() => setEnding(false)}
          onDone={(saved) => { setEnding(false); setForm(formFromAccount(saved)); onSaved(saved); }} onSessionExpired={onSessionExpired} />
      )}
    </li>
  );
}

const emptyAccount = formFromAccount({});

// With focusId, only that account is shown (opened from its own "Set up account" button),
// with a button to see every account.
export function AccountManager({ accounts, trades = [], focusId = null, onChange, onReload, onDeleted, onTradesCleared, onClose, onSessionExpired }) {
  const { t, tr } = useI18n();
  const [focus, setFocus] = useState(focusId);
  const focused = focus ? accounts.find((a) => a._id === focus) : null;
  const [newAccount, setNewAccount] = useState(emptyAccount);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [adding, setAdding] = useState(false);
  const [confirmAll, setConfirmAll] = useState(false);
  const [clearingAll, setClearingAll] = useState(false);
  const [allNotice, setAllNotice] = useState('');
  const [allError, setAllError] = useState('');
  const totalTrades = accounts.reduce((sum, a) => sum + (a.tradeCount || 0), 0);

  async function clearAll() {
    setClearingAll(true);
    setAllError('');
    try {
      const { deletedTrades } = await api('/trades?confirm=all', { method: 'DELETE' });
      setConfirmAll(false);
      setAllNotice(t('Deleted {trades} from all your accounts.', { trades: tradesText(deletedTrades) }));
      onTradesCleared('all');
    } catch (err) {
      if (err.status === 401) return onSessionExpired();
      setAllError(err.message);
    } finally {
      setClearingAll(false);
    }
  }
  const dialog = useRef(null);

  useEffect(() => {
    dialog.current?.focus();
    function onKey(e) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function add(e) {
    e.preventDefault();
    setAdding(true);
    setError('');
    setNotice('');
    try {
      const created = await api('/accounts', { method: 'POST', body: bodyFromForm(newAccount) });
      setNewAccount(emptyAccount);
      if (created.movedTrades) {
        setNotice(linkMessage(created));
        onReload();
      } else {
        onChange([...accounts, created]);
      }
    } catch (err) {
      if (err.status === 401) return onSessionExpired();
      setError(err.message);
    } finally {
      setAdding(false);
    }
  }

  // Past attempts are listed under their account rather than as accounts of their own.
  const sorted = accounts.filter((a) => !a.attemptOf).sort((a, b) => Number(a.archived) - Number(b.archived));
  const attemptsOf = (id) => accounts.filter((a) => a.attemptOf === id).sort((a, b) => b.attemptNumber - a.attemptNumber);

  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div ref={dialog} tabIndex={-1} className="modal modal-wide" role="dialog" aria-modal="true" aria-labelledby="accounts-title">
        <div className="modal-header">
          <h2 id="accounts-title">{focused ? t('Set up {name}', { name: focused.name }) : t('Accounts')}</h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label={t('Close')}>&times;</button>
        </div>
        {focused ? (
          <p className="hint account-focus-hint">
            {tr("Set a <0>starting balance</0> to track this account's balance, and for prop accounts a profit target and max loss limit (or pick a preset) to see how close you are to passing or blowing it.",
              [(x) => <strong>{x}</strong>])}{' '}
            <button type="button" className="link-button" onClick={() => setFocus(null)}>{t('Show all accounts')}</button>
          </p>
        ) : (
        <p className="hint">
          {tr("Add the <0>account number</0> from your CSV and every trade from that account is linked to it automatically, now and on future imports. Set a <1>starting balance</1> to track the account's balance, and for prop accounts a profit target and max loss limit (or pick a preset) to see how close you are to passing or blowing it. Archive accounts you no longer trade to hide them while keeping their history.",
            [(x) => <strong>{x}</strong>, (x) => <strong>{x}</strong>])}
        </p>
        )}

        <ul className="account-list">
          {(focused ? [focused] : sorted).map((a) => (
            <AccountRow
              key={a._id}
              account={a}
              pastAttempts={attemptsOf(a._id)}
              trades={trades}
              onAttemptStarted={onReload}
              onSaved={(saved) => onChange(accounts.map((x) => (x._id === saved._id ? saved : x)))}
              onMerged={onReload}
              onFeesSaved={({ account: saved, trades: changed }) => {
                onChange(accounts.map((x) => (x._id === saved._id ? { ...x, ...saved } : x)));
                if (changed && changed.length) onReload();
              }}
              onDeleted={onDeleted}
              onTradesCleared={onTradesCleared}
              onSessionExpired={onSessionExpired}
            />
          ))}
        </ul>

        {!focused && (
        <form className="account-add" onSubmit={add}>
          <span className="modal-section-label">{t('Add an account')}</span>
          <AccountFields form={newAccount} onChange={setNewAccount} />
          {error && <p className="error">{error}</p>}
          {notice && <p className="success">{notice}</p>}
          <button type="submit" className="small" disabled={adding || !newAccount.name.trim()}>
            {adding ? t('Adding…') : t('Add account')}
          </button>
        </form>
        )}

        {!focused && (totalTrades > 0 || allNotice) && (
          <div className="account-danger-zone">
            <span className="modal-section-label">{t('Start over')}</span>
            {confirmAll ? (
              <DangerConfirm title={t('Delete every trade in every account?')} phrase={t('delete all trades')} busy={clearingAll}
                confirmLabel={t('Delete {trades}', { trades: tradesText(totalTrades) })} onConfirm={clearAll} onCancel={() => setConfirmAll(false)}>
                {t("This permanently deletes {trades} across all your accounts. Your accounts and their rules stay, and so do your journal notes, routine and goals. This can't be undone.", { trades: allTradesText(totalTrades) })}
              </DangerConfirm>
            ) : (
              <div className="account-danger-row">
                <span className="hint">{t('Remove every trade from every account. To clear just one account, use its Clear trades button above.')}</span>
                <button type="button" className="secondary small" onClick={() => { setConfirmAll(true); setAllNotice(''); }}
                  disabled={!totalTrades}>
                  {t('Delete all trades')}
                </button>
              </div>
            )}
            {allError && <p className="error">{allError}</p>}
            {allNotice && <p className="success">{allNotice}</p>}
          </div>
        )}
      </div>
    </div>
  );
}

// Account numbers are matched ignoring spaces, dashes, dots and capital letters, so
// "APEX-12345" in a file finds "apex 12345" in the journal.
export function normNumber(number) {
  return String(number || '').toUpperCase().replace(/[\s\-_.#]/g, '');
}

// The account a file's number belongs to: its account number, a number linked in an earlier
// import, or its name. Past attempts are skipped, and an active account wins over an archived one.
export function findAccountForNumber(number, accounts) {
  const n = normNumber(number);
  if (!n) return null;
  const candidates = accounts.filter((a) => !a.attemptOf);
  const hit = (a) => normNumber(a.externalId) === n || (a.importNumbers || []).some((x) => normNumber(x) === n) || normNumber(a.name) === n;
  const found = candidates.filter(hit);
  return found.find((a) => !a.archived) || found[0] || null;
}

// The last account each platform's file went into (this browser only), for files without
// account numbers.
const LAST_IMPORT_KEY = 'mm-import-last';
function lastImportAccount(platform) {
  try {
    return (JSON.parse(localStorage.getItem(LAST_IMPORT_KEY)) || {})[platform] || '';
  } catch {
    return '';
  }
}
function rememberImportAccount(platform, accountId) {
  if (!platform || platform === 'Other' || !accountId) return;
  try {
    const all = JSON.parse(localStorage.getItem(LAST_IMPORT_KEY)) || {};
    localStorage.setItem(LAST_IMPORT_KEY, JSON.stringify({ ...all, [platform]: accountId }));
  } catch {
    // Not remembered; the trader picks the account next time.
  }
}

const STOCK_BROKERS = ['Webull', 'Schwab', 'Robinhood'];
const FUTURES_PLATFORMS = ['TopstepX', 'Tradovate', 'NinjaTrader', 'Rithmic', 'Tradesea'];
const isFuturesSymbol = (symbol) => /[FGHJKMNQUVXZ]\d{1,2}$/.test(String(symbol || '').toUpperCase());

// Reasons a file's trades may be going into the wrong account, as plain sentences.
// Once the file's number matched the account (`matched`), only the stocks-vs-futures check is left.
export function importWarnings(account, trades, platform, number = null, matched = false) {
  if (!account || !trades.length) return [];
  const out = [];
  const name = account.name;
  const nameBroker = STOCK_BROKERS.find((b) => name.toLowerCase().includes(b.toLowerCase()));
  if (matched) {
    // The account number says it's this account.
  } else if (platform !== 'Other' && account.platform && account.platform !== 'Other' && account.platform !== platform) {
    out.push(translateNow('This file is from {platform}, but {name} is a {other} account.').replace('{platform}', platform).replace('{name}', name).replace('{other}', account.platform));
  } else if (STOCK_BROKERS.includes(platform) && FUTURES_PLATFORMS.includes(account.platform)) {
    out.push(translateNow('This file is from {platform}, but {name} is a {other} account.').replace('{platform}', platform).replace('{name}', name).replace('{other}', account.platform));
  } else if (platform !== 'Other' && nameBroker && nameBroker !== platform) {
    out.push(translateNow('This file is from {platform}, but the account is named {name}.').replace('{platform}', platform).replace('{name}', name));
  }
  // Tradesea's symbols have no contract month ("ES"), so a futures platform's file counts as futures.
  const stocks = FUTURES_PLATFORMS.includes(platform) ? 0 : trades.filter((x) => !isFuturesSymbol(x.symbol)).length;
  if ((account.type === 'Evaluation' || account.type === 'Funded' || account.type === 'Live') && stocks > trades.length / 2) {
    out.push(translateNow('These look like stock trades, but {name} is a futures prop account.').replace('{name}', name));
  }
  if (number && account.externalId && normNumber(account.externalId) !== normNumber(number)) {
    out.push(translateNow('{name} already has account number {saved}, and this file says {number}.').replace('{name}', name).replace('{saved}', maskNumber(account.externalId)).replace('{number}', maskNumber(number)));
  }
  return out;
}

// Default account choices for a freshly read CSV: known numbers map to their
// accounts, new numbers get a suggested name, and trades without a number go to
// the currently selected account (or, with "All accounts", the last account this
// platform's file went into). With nothing to go on the trader has to pick one
// (empty accountId), so trades never land in the wrong account by default.
export function needsAccountChoice(csvResult, assignment) {
  return Boolean(assignment) && csvResult.trades.some((t) => !t.accountNumber) && !assignment.fallback.accountId;
}

export function initialAssignment(csvResult, accounts, selected) {
  const platform = csvResult.format ? csvResult.format.platform : 'Other';
  const numbers = {};
  for (const t of csvResult.trades) {
    if (t.accountNumber && !(t.accountNumber in numbers)) {
      const existing = findAccountForNumber(t.accountNumber, accounts);
      numbers[t.accountNumber] = existing ? { accountId: existing._id, matched: true } : { accountId: 'new', name: suggestedName(t.accountNumber, platform) };
    }
  }
  const active = accounts.filter((a) => !a.archived);
  const remembered = lastImportAccount(platform);
  const fallbackId = selected !== 'all' && selected !== 'multi' && active.some((a) => a._id === selected) ? selected
    : active.some((a) => a._id === remembered) ? remembered
      : active.length ? '' : 'new';
  return {
    platform,
    numbers,
    fallback: { accountId: fallbackId, name: platform !== 'Other' ? translateNow('{platform} account').replace('{platform}', platform) : translateNow('My account') },
  };
}

function ImportWarnings({ list }) {
  if (!list.length) return null;
  return (
    <ul className="import-warnings" role="alert">
      {list.map((w) => <li key={w}><span aria-hidden="true">⚠</span> {w}</li>)}
    </ul>
  );
}

// Lets the trader confirm or name the accounts an import's trades go into.
export function ImportAccounts({ csvResult, accounts, assignment, onChange }) {
  const { t } = useI18n();
  const numbers = Object.keys(assignment.numbers);
  const unnumberedTrades = csvResult.trades.filter((x) => !x.accountNumber);
  const unnumbered = unnumberedTrades.length;
  const active = accounts.filter((a) => !a.archived && !a.attemptOf);
  const tradesFor = (n) => csvResult.trades.filter((x) => x.accountNumber === n);
  // An account already chosen for another number in this file isn't offered again.
  const taken = (number) => new Set(numbers.filter((n) => n !== number).map((n) => assignment.numbers[n].accountId));

  function setNumber(number, changes) {
    onChange({ ...assignment, numbers: { ...assignment.numbers, [number]: { ...assignment.numbers[number], ...changes } } });
  }

  function setFallback(changes) {
    onChange({ ...assignment, fallback: { ...assignment.fallback, ...changes } });
  }

  if (!csvResult.trades.length) return null;
  const fallbackAccount = accounts.find((x) => x._id === assignment.fallback.accountId);

  return (
    <div className="import-accounts">
      <span className="modal-section-label">{t('Accounts')}</span>
      {numbers.map((number) => {
        const a = assignment.numbers[number];
        const existing = accounts.find((x) => x._id === a.accountId);
        const list = tradesFor(number);
        return (
          <div key={number} className="import-account-block">
            <div className="import-account-row">
              <span className="account-number">{maskNumber(number)}</span>
              <span className="import-account-count">{tradesText(list.length)}</span>
              {a.matched && existing ? (
                <span>
                  &rarr; <strong>{existing.name}</strong>
                  {existing.archived && ` ${t('(archived)')}`}
                  <span className="import-match">{t('Matched by account number')}</span>
                </span>
              ) : (
                <>
                  <select value={a.accountId} onChange={(e) => setNumber(number, { accountId: e.target.value })}
                    aria-label={t('Account for {number}', { number: maskNumber(number) })}>
                    <option value="new">{t('+ New account…')}</option>
                    {active.filter((x) => !taken(number).has(x._id)).map((x) => <option key={x._id} value={x._id}>{x.name}</option>)}
                  </select>
                  {a.accountId === 'new' ? (
                    <input value={a.name} onChange={(e) => setNumber(number, { name: e.target.value })} maxLength={60}
                      aria-label={t('New account name')} placeholder={t('Account name')} />
                  ) : (
                    <span className="field-hint">{t('Future files with this number go to this account automatically.')}</span>
                  )}
                </>
              )}
            </div>
            <ImportWarnings list={a.matched ? importWarnings(existing, list, assignment.platform, null, true) : importWarnings(existing, list, assignment.platform, number)} />
          </div>
        );
      })}
      {unnumbered > 0 && (
        <div className="import-account-block">
          <div className={`import-account-row${assignment.fallback.accountId ? '' : ' needs-choice'}`}>
            <span className="import-account-question">
              {numbers.length
                ? t('Which account are the {trades} without an account number from?', { trades: tradesText(unnumbered) })
                : t('Which account are these trades from?')}
            </span>
            <select value={assignment.fallback.accountId} onChange={(e) => setFallback({ accountId: e.target.value })}
              aria-label={t('Account to import into')}>
              {!assignment.fallback.accountId && <option value="" disabled>{t('Choose an account…')}</option>}
              {active.map((x) => <option key={x._id} value={x._id}>{x.name}</option>)}
              <option value="new">{t('+ New account…')}</option>
            </select>
            {assignment.fallback.accountId === 'new' && (
              <input value={assignment.fallback.name} onChange={(e) => setFallback({ name: e.target.value })}
                maxLength={60} aria-label={t('New account name')} placeholder={t('Account name')} />
            )}
          </div>
          <ImportWarnings list={importWarnings(fallbackAccount, unnumberedTrades, assignment.platform)} />
        </div>
      )}
    </div>
  );
}

// Creates any new accounts the import needs and returns the trades with their
// accountId filled in, plus the accounts that were created.
export async function resolveImportAccounts(trades, assignment) {
  const created = [];
  const idForNumber = {};
  for (const [number, a] of Object.entries(assignment.numbers)) {
    if (a.accountId !== 'new') {
      idForNumber[number] = a.accountId;
      // A number the trader linked by hand is saved on the account, so the next file matches.
      // If it can't be saved (another account has it), the trades still go where the trader chose.
      if (!a.matched) await api(`/accounts/${a.accountId}`, { method: 'PATCH', body: { linkNumber: number } }).catch(() => null);
      continue;
    }
    const account = await api('/accounts', {
      method: 'POST',
      body: { name: a.name.trim() || suggestedName(number, assignment.platform), externalId: number, platform: assignment.platform },
    });
    created.push(account);
    idForNumber[number] = account._id;
  }
  let fallbackId = assignment.fallback.accountId;
  if (fallbackId === 'new' && trades.some((t) => !t.accountNumber)) {
    const account = await api('/accounts', {
      method: 'POST',
      body: { name: assignment.fallback.name.trim() || translateNow('My account'), platform: assignment.platform },
    });
    created.push(account);
    fallbackId = account._id;
  }
  if (trades.some((t) => !t.accountNumber)) rememberImportAccount(assignment.platform, fallbackId);
  const resolved = trades.map(({ accountNumber, ...t }) => ({
    ...t,
    ...(accountNumber ? { sourceAccount: accountNumber } : {}),
    accountId: accountNumber ? idForNumber[accountNumber] : fallbackId,
  }));
  return { trades: resolved, created };
}
